<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue'
import { useLotteryStore } from '../stores/lottery.js'

const props = defineProps({
  batch: { type: Object, default: null },
  highlightTicket: { type: Number, default: null }, // globalt nr fra draw
  username: { type: String, default: '' },
  title: { type: String, default: 'Loddark' },
})

const emit = defineEmits(['close'])
const lotteryStore = useLotteryStore()

const toNumber = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/* ---------------------------
   Image loading (blob)
---------------------------- */
const imageUrl = ref(null)
const imageLoading = ref(false)
const imageError = ref('')

const revokeImageUrl = () => {
  if (imageUrl.value) {
    URL.revokeObjectURL(imageUrl.value)
    imageUrl.value = null
  }
}

const loadImage = async (lotteryId, batchId) => {
  imageLoading.value = true
  imageError.value = ''
  revokeImageUrl()

  try {
    const res = await lotteryStore.fetchBatchImage(lotteryId, batchId)

    if (!res?.success) {
      imageError.value = res?.error ?? 'Kunne ikke hente bilde.'
      return
    }

    if (!res?.data || res.data.size === 0) {
      imageError.value = 'Bildedata mangler i responsen.'
      return
    }

    imageUrl.value = URL.createObjectURL(res.data)
  } catch (err) {
    imageError.value = err?.response?.data?.detail ?? 'Kunne ikke hente bilde.'
  } finally {
    imageLoading.value = false
  }
}

watch(
  () => [props.batch?.lottery_id, props.batch?.id],
  async ([lotteryId, batchId]) => {
    if (!lotteryId || !batchId) {
      revokeImageUrl()
      return
    }
    await loadImage(lotteryId, batchId)
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  revokeImageUrl()
})

/* ---------------------------
   Ticket mapping: GLOBAL -> LOCAL
   Viser alltid fysisk ark 1..N
---------------------------- */
const globalStart = computed(() => toNumber(props.batch?.global_start))
const globalEnd = computed(() => toNumber(props.batch?.global_end))
const rangeStart = computed(() => globalStart.value ?? toNumber(props.batch?.ticket_range_start))
const rangeEnd = computed(() => globalEnd.value ?? toNumber(props.batch?.ticket_range_end))
const soldCount = computed(() => toNumber(props.batch?.number_of_tickets) ?? 0)
const sheetSize = computed(() => {
  if (rangeStart.value !== null && rangeEnd.value !== null && rangeEnd.value >= rangeStart.value) {
    return rangeEnd.value - rangeStart.value + 1
  }
  return soldCount.value
})
const ticketRange = computed(() => {
  if (!sheetSize.value || sheetSize.value < 1) return []
  return Array.from({ length: sheetSize.value }, (_, i) => i + 1)
})

const isGlobalMode = computed(() => {
  return rangeStart.value !== null && rangeEnd.value !== null
})

const toLocalTicket = (num) => {
  if (num === null) return null

  // Har vi global range? Tolker som globalt først (viktig!)
  if (isGlobalMode.value) {
    if (num >= rangeStart.value && num <= rangeEnd.value) {
      return num - rangeStart.value + 1
    }
    // fallback: hvis backend faktisk sender lokalt
    if (num >= 1 && num <= sheetSize.value) {
      return num
    }
    return null
  }

  // Uten global info: antar lokalt
  if (num >= 1 && num <= sheetSize.value) return num
  return null
}

const soldTickets = computed(() => {
  const raw = Array.isArray(props.batch?.sold_tickets) ? props.batch.sold_tickets : []
  return [
    ...new Set(
      raw
        .map((v) => toNumber(v))
        .map((v) => toLocalTicket(v))
        .filter((v) => v !== null),
    ),
  ]
})

// highlight fra draw er globalt i deres API
const localHighlightTicket = computed(() => {
  const h = toNumber(props.highlightTicket)
  if (h === null) return null
  if (isGlobalMode.value && h >= rangeStart.value && h <= rangeEnd.value) {
    return h - rangeStart.value + 1
  }
  // fallback
  if (h >= 1 && h <= sheetSize.value) return h
  return null
})

const isHighlighted = (num) => localHighlightTicket.value !== null && num === localHighlightTicket.value
const isSold = (num) => soldTickets.value.includes(num)
</script>

<template>
  <div class="modal-overlay" @click.self="emit('close')">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ title }}</h2>
        <button class="close-btn" @click="emit('close')">✕</button>
      </div>

      <div v-if="batch" class="modal-body">
        <div v-if="username" class="info-row">
          <span class="label">Bruker:</span>
          <span class="value">{{ username }}</span>
        </div>
        <div class="info-row">
          <span class="label">Solgte lodd:</span>
          <span class="value">{{ soldTickets.length }}</span>
        </div>
        <div class="info-row">
          <span class="label">Fysisk ark:</span>
          <span class="value">1 – {{ sheetSize }}</span>
        </div>
        <div class="info-row">
          <span class="label">Globalområde:</span>
          <span class="value">{{ rangeStart ?? '-' }} – {{ rangeEnd ?? '-' }}</span>
        </div>
        <div class="info-row">
          <span class="label">Loddark-ID:</span>
          <span class="value">#{{ batch.id }}</span>
        </div>

        <!-- Bildet av loddvarket -->
<div class="image-section">
  <h3>Loddark</h3>

  <div v-if="imageLoading" class="no-image">Laster bilde...</div>

  <img
    v-else-if="imageUrl"
    :src="imageUrl"
    :alt="`Loddark for ${username}`"
    class="ticket-image"
  />

  <div v-else class="no-image">
    Ingen bilde tilgjengelig ({{ batch.filename }})
    <div v-if="imageError" style="margin-top: 0.5rem; color: #b91c1c;">
      {{ imageError }}
    </div>
  </div>
</div>

        <!-- Oversikt over avkryssede lodd -->
        <div class="tickets-section">
          <h3>
            Avkryssede lodd
            <span class="sold-count">({{ soldTickets.length }} av {{ sheetSize }})</span>
          </h3>

          <div v-if="localHighlightTicket !== null" class="highlight-info">
            🏆 Vinnerloddet er <strong>#{{ localHighlightTicket }}</strong>
          </div>

          <div class="ticket-grid">
            <span
                v-for="num in ticketRange"
                :key="num"
                class="ticket-chip"
                :class="{
                sold: isSold(num),
                winner: isHighlighted(num),
                unsold: !isSold(num),
              }"
                :title="isHighlighted(num) ? '🏆 Vinnerloddet' : isSold(num) ? 'Avkrysset (solgt)' : 'Ikke solgt'"
            >
              {{ num }}
            </span>
          </div>

          <div class="legend">
            <span class="chip-example sold">Avkrysset/solgt</span>
            <span class="chip-example unsold">Ikke solgt</span>
            <span v-if="localHighlightTicket !== null" class="chip-example winner">🏆 Vinner</span>
          </div>
        </div>
      </div>

      <div v-else class="loading-body">Laster inn...</div>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 12px;
  width: min(800px, 95vw);
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}

.modal-header h2 {
  margin: 0;
  color: #111827;
  font-size: 1.25rem;
}

.close-btn {
  border: none;
  background: #f3f4f6;
  color: #374151;
  border-radius: 6px;
  padding: 0.4rem 0.7rem;
  cursor: pointer;
  font-size: 1rem;
}

.close-btn:hover {
  background: #e5e7eb;
}

.modal-body {
  padding: 1.5rem;
  display: grid;
  gap: 1.25rem;
}

.loading-body {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
}

.info-row {
  display: flex;
  gap: 0.5rem;
  color: #111827;
}

.label {
  font-weight: bold;
  min-width: 130px;
}

.value {
  color: #374151;
}

.image-section h3,
.tickets-section h3 {
  color: #111827;
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.ticket-image {
  width: 100%;
  max-height: 400px;
  object-fit: contain;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
}

.no-image {
  padding: 2rem;
  text-align: center;
  color: #9ca3af;
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
}

.sold-count {
  font-weight: normal;
  color: #6b7280;
  font-size: 0.875rem;
  margin-left: 0.5rem;
}

.highlight-info {
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 6px;
  padding: 0.6rem 1rem;
  color: #92400e;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.ticket-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.ticket-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.5rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: default;
  border: 1px solid transparent;
}

.ticket-chip.sold {
  background: #dcfce7;
  color: #166534;
  border-color: #86efac;
}

.ticket-chip.unsold {
  background: #f3f4f6;
  color: #9ca3af;
  border-color: #e5e7eb;
}

.ticket-chip.winner {
  background: #fbbf24;
  color: #1c1917;
  border-color: #f59e0b;
  box-shadow: 0 0 0 2px #fde68a;
  transform: scale(1.15);
}

.legend {
  display: flex;
  gap: 1rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;
}

.chip-example {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid transparent;
}

.chip-example.sold {
  background: #dcfce7;
  color: #166534;
  border-color: #86efac;
}

.chip-example.unsold {
  background: #f3f4f6;
  color: #9ca3af;
  border-color: #e5e7eb;
}

.chip-example.winner {
  background: #fbbf24;
  color: #1c1917;
  border-color: #f59e0b;
}
</style>